import { useState } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styles from './AlertsPage.module.css';
import { useNavigate } from 'react-router-dom';
import { BellOff } from 'lucide-react';
import { Heading, Text, Button, EmptyState } from '../components/atoms';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { apiClient } from '../lib/apiClient';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { ReefGuardFindings } from '../components/reefGuard/ReefGuardFindings';

/**
 * A parameter alert from GET /api/v1/alerts: the latest measurement of a
 * parameter in one of the user's aquariums lies outside its target range.
 * The list spans all of the user's aquariums — no aquarium needs to be
 * selected. `id` is the measurement id.
 */
interface Alert {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  direction: 'low' | 'high';
  parameterId: string;
  parameter: string;
  value: number;
  unit: string;
  threshold: number;
  aquariumId: string;
  aquariumName: string;
  createdAt: string;
  acknowledged: boolean;
}

interface AlertsResponse {
  data: Alert[];
}

async function fetchAlerts(): Promise<Alert[]> {
  const response = await apiClient.get<AlertsResponse>('/api/v1/alerts');
  return Array.isArray(response?.data) ? response.data : [];
}

async function acknowledgeAlert(alertId: string): Promise<void> {
  await apiClient.post(`/api/v1/alerts/${alertId}/acknowledge`, {});
}

async function dismissAlert(alertId: string): Promise<void> {
  await apiClient.delete(`/api/v1/alerts/${alertId}`);
}

function getSeverityCardClass(severity: Alert['severity']): string {
  if (severity === 'critical') return styles.alertCardCritical;
  if (severity === 'warning') return styles.alertCardWarning;
  return styles.alertCardInfo;
}

function getSeverityBadgeClass(severity: Alert['severity']): string {
  if (severity === 'critical') return styles.badgeCritical;
  if (severity === 'warning') return styles.badgeWarning;
  return styles.badgeInfo;
}

function AlertCard({ alert, onAcknowledge, onDismiss, isActioning }: {
  alert: Alert;
  onAcknowledge: (id: string) => void;
  onDismiss: (id: string) => void;
  isActioning: boolean;
}) {
  const { t } = useTranslation();

  const formattedDate = new Date(alert.createdAt).toLocaleDateString();
  const formattedTime = new Date(alert.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <article className={`${styles.alertCard} ${getSeverityCardClass(alert.severity)} ${alert.acknowledged ? styles.alertCardAcknowledged : ''}`}>
      <div className={styles.alertCardHeader}>
        <div className={styles.alertCardMeta}>
          <span className={`${styles.badge} ${getSeverityBadgeClass(alert.severity)}`}>
            {t(`alerts.severity.${alert.severity}`)}
          </span>
          {alert.aquariumName && (
            <span className={styles.aquariumChip}>{alert.aquariumName}</span>
          )}
        </div>
        <Text variant="caption" className={styles.alertTimestamp}>
          {formattedDate} {formattedTime}
        </Text>
      </div>

      <div className={styles.alertCardBody}>
        <Heading level={2} className={styles.alertTitle}>
          {t(alert.direction === 'low' ? 'alerts.titleLow' : 'alerts.titleHigh', { parameter: alert.parameter })}
        </Heading>
        <Text variant="body" className={styles.alertMessage}>
          {t(alert.direction === 'low' ? 'alerts.messageLow' : 'alerts.messageHigh', {
            value: `${alert.value}${alert.unit ? ` ${alert.unit}` : ''}`,
            threshold: `${alert.threshold}${alert.unit ? ` ${alert.unit}` : ''}`,
          })}
        </Text>

        <div className={styles.alertParameterStrip}>
          <div className={styles.parameterDetail}>
            <Text variant="caption" className={styles.parameterLabel}>
              {t('alerts.parameter')}
            </Text>
            <Text variant="body" className={styles.parameterValue}>
              {alert.parameter}
            </Text>
          </div>
          <div className={styles.parameterDetail}>
            <Text variant="caption" className={styles.parameterLabel}>
              {t('alerts.measured')}
            </Text>
            <Text variant="body" className={`${styles.parameterValue} ${styles.parameterValueHighlight}`}>
              {alert.value}{alert.unit ? ` ${alert.unit}` : ''}
            </Text>
          </div>
          <div className={styles.parameterDetail}>
            <Text variant="caption" className={styles.parameterLabel}>
              {t('alerts.threshold')}
            </Text>
            <Text variant="body" className={styles.parameterValue}>
              {alert.threshold}{alert.unit ? ` ${alert.unit}` : ''}
            </Text>
          </div>
        </div>
      </div>

      {!alert.acknowledged && (
        <div className={styles.alertCardActions}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onAcknowledge(alert.id)}
            disabled={isActioning}
          >
            {t('alerts.acknowledge')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDismiss(alert.id)}
            disabled={isActioning}
          >
            {t('alerts.dismiss')}
          </Button>
        </div>
      )}

      {alert.acknowledged && (
        <div className={styles.alertCardFooter}>
          <span className={styles.acknowledgedBadge}>{t('alerts.acknowledged')}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDismiss(alert.id)}
            disabled={isActioning}
          >
            {t('alerts.dismiss')}
          </Button>
        </div>
      )}
    </article>
  );
}

type FilterType = 'all' | 'critical' | 'warning' | 'info' | 'unacknowledged';

export function AlertsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [actioningIds, setActioningIds] = useState<Set<string>>(new Set());

  const { data: alerts = [], isLoading, isError, refetch } = useQuery<Alert[]>({
    queryKey: ['alerts'],
    queryFn: fetchAlerts,
  });

  const acknowledgeMutation = useMutation({
    mutationFn: acknowledgeAlert,
    onMutate: (alertId: string) => {
      setActioningIds(prev => new Set(prev).add(alertId));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onSettled: (_data, _error, alertId: string) => {
      setActioningIds(prev => {
        const next = new Set(prev);
        next.delete(alertId);
        return next;
      });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: dismissAlert,
    onMutate: (alertId: string) => {
      setActioningIds(prev => new Set(prev).add(alertId));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onSettled: (_data, _error, alertId: string) => {
      setActioningIds(prev => {
        const next = new Set(prev);
        next.delete(alertId);
        return next;
      });
    },
  });

  const handleAcknowledge = (alertId: string): void => {
    acknowledgeMutation.mutate(alertId);
  };

  const handleDismiss = (alertId: string): void => {
    dismissMutation.mutate(alertId);
  };

  const handleAcknowledgeAll = (): void => {
    const unacknowledged = alerts.filter(a => !a.acknowledged);
    unacknowledged.forEach(a => acknowledgeMutation.mutate(a.id));
  };

  const filteredAlerts = alerts.filter(alert => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'unacknowledged') return !alert.acknowledged;
    return alert.severity === activeFilter;
  });

  const criticalCount = alerts.filter(a => a.severity === 'critical' && !a.acknowledged).length;
  const warningCount = alerts.filter(a => a.severity === 'warning' && !a.acknowledged).length;
  const unacknowledgedCount = alerts.filter(a => !a.acknowledged).length;

  const filters: { key: FilterType; label: string; count?: number }[] = [
    { key: 'all', label: t('alerts.filterAll'), count: alerts.length },
    { key: 'unacknowledged', label: t('alerts.filterUnacknowledged'), count: unacknowledgedCount },
    { key: 'critical', label: t('alerts.severity.critical'), count: criticalCount },
    { key: 'warning', label: t('alerts.severity.warning'), count: warningCount },
    { key: 'info', label: t('alerts.severity.info') },
  ];

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent>
          <PageHeader
            title={t('alerts.title')}
            description={t('alerts.subtitle')}
            actions={
              unacknowledgedCount > 0 ? (
                <Button
                  variant="secondary"
                  onClick={handleAcknowledgeAll}
                  disabled={acknowledgeMutation.isPending}
                >
                  {t('alerts.acknowledgeAll')}
                </Button>
              ) : undefined
            }
          />

          <ReefGuardFindings />

          <div className={styles.summaryStrip}>
            <div className={styles.summaryCard}>
              <Text variant="caption" className={styles.summaryLabel}>{t('alerts.filterAll')}</Text>
              <Text as="span" className={styles.summaryCount}>{alerts.length}</Text>
            </div>
            <div className={`${styles.summaryCard} ${styles.summaryCardCritical}`}>
              <Text variant="caption" className={styles.summaryLabel}>{t('alerts.severity.critical')}</Text>
              <Text as="span" className={styles.summaryCount}>{criticalCount}</Text>
            </div>
            <div className={`${styles.summaryCard} ${styles.summaryCardWarning}`}>
              <Text variant="caption" className={styles.summaryLabel}>{t('alerts.severity.warning')}</Text>
              <Text as="span" className={styles.summaryCount}>{warningCount}</Text>
            </div>
            <div className={styles.summaryCard}>
              <Text variant="caption" className={styles.summaryLabel}>{t('alerts.unacknowledged')}</Text>
              <Text as="span" className={styles.summaryCount}>{unacknowledgedCount}</Text>
            </div>
          </div>

          <div className={styles.filterBar}>
            {filters.map(filter => (
              <Button
                key={filter.key}
                variant="ghost"
                className={`${styles.filterChip} ${activeFilter === filter.key ? styles.filterChipActive : ''}`}
                onClick={() => setActiveFilter(filter.key)}
                type="button"
              >
                {filter.label}
                {filter.count !== undefined && (
                  <span className={styles.filterCount}>{filter.count}</span>
                )}
              </Button>
            ))}
          </div>

          <div className={styles.alertsContent}>
            {isLoading && (
              <LiveRegion busy className={styles.loadingState}>
                <div className={styles.loadingSpinner} />
                <Text variant="body" className={styles.loadingText}>{t('alerts.loading')}</Text>
              </LiveRegion>
            )}

            {isError && (
              <LoadErrorState
                title={t('alerts.errorTitle')}
                titleLevel={2}
                message={t('alerts.errorMessage')}
                onRetry={() => void refetch()}
                retryLabel={t('alerts.retry')}
              />
            )}

            {!isLoading && !isError && filteredAlerts.length === 0 && (
              activeFilter === 'all' ? (
                <EmptyState
                  icon={<BellOff size={48} strokeWidth={1.5} />}
                  headline={t('alerts.emptyTitle')}
                  description={t('alerts.emptyMessage')}
                  actionLabel={t('alerts.emptyAction')}
                  onAction={() => navigate('/aquariums')}
                />
              ) : (
                <EmptyState
                  icon={<BellOff size={48} strokeWidth={1.5} />}
                  headline={t('alerts.emptyFilterTitle')}
                  description={t('alerts.emptyFilterMessage')}
                  actionLabel={t('alerts.clearFilter')}
                  onAction={() => setActiveFilter('all')}
                />
              )
            )}

            {!isLoading && !isError && filteredAlerts.length > 0 && (
              <div className={styles.alertsList}>
                {filteredAlerts.map(alert => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    onAcknowledge={handleAcknowledge}
                    onDismiss={handleDismiss}
                    isActioning={actioningIds.has(alert.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </PageContent>
      }
    />
  );
}

export default AlertsPage;
